import type { ReactNode } from "react";

import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";

import { CARET_PX, ROW_GAP } from "./widget-disclosure-row";

/** The header line's height: one `xs` text line with its air, so a
 *  skeleton's header slot holds exactly what the settled header takes. */
export const LIST_HEADER_PX = 24;

/** One trailing figure column of a list card: its label, named ONCE in the
 *  header, and the fixed width every row's cell shares so figures align. */
export type WidgetListColumn = {
  key: string;
  label: string;
  width: number;
  /** The narrowest body this column survives in, in px. Columns fold from
   *  the end inward as a card narrows; a column with no floor always shows.
   *  Measured off the body, never the grid span: a span is a fraction of a
   *  grid whose pixel size the card cannot know. */
  minWidth?: number;
};

/**
 * The columns a body `bodyWidth` px wide keeps, in order. Zero (unmeasured)
 * keeps only the always-on columns, so a first paint never flashes a column
 * the width then drops.
 */
export function widgetListColumnsFor<T extends WidgetListColumn>(
  columns: readonly T[],
  bodyWidth: number,
): T[] {
  return columns.filter((column) => bodyWidth >= (column.minWidth ?? 0));
}

export type WidgetListHeaderProps = Omittable<{
  /** The identity column's label ("Member", "Workflow"). */
  identity: ReactNode;
  /** The figure columns, in row order — pass the ones the width keeps. */
  columns: readonly WidgetListColumn[];
  /** The rows below lead with a disclosure caret ({@link WidgetDisclosureRow});
   *  the header leaves that slot empty so its cells sit over the rows'. */
  caretSlot?: boolean;
}>;

/**
 * The header line that names a list card's columns once, so its rows carry
 * figures alone — one line each instead of a figure over a word. Cells share
 * the rows' gap and widths ({@link WidgetListRow} / {@link WidgetDisclosureRow}),
 * so a header cell sits exactly over the row cell it names.
 */
export function WidgetListHeader({ identity, columns, caretSlot = false }: WidgetListHeaderProps) {
  return (
    <Box direction="row" align="center" gap={ROW_GAP} minHeight={LIST_HEADER_PX} shrink={false}>
      {caretSlot ? <Box width={CARET_PX} shrink={false} /> : null}
      <Box fill="flex" shrink minWidth={0}>
        <Text size="xs" weight="medium" variant="faint" truncate>
          {identity}
        </Text>
      </Box>
      {columns.map((column) => (
        <Box key={column.key} width={column.width} shrink={false} align="end">
          <Text size="xs" weight="medium" variant="faint" truncate>
            {column.label}
          </Text>
        </Box>
      ))}
    </Box>
  );
}
